import { useState, useEffect, useCallback } from 'react';
import { 
  Warranty, 
  WarrantyClaim, 
  CreateWarrantyData, 
  CreateWarrantyClaimData, 
  WarrantyQueryParams, 
  WarrantyClaimQueryParams,
  WarrantyStats 
} from '../types/warranty';
import { warrantyService } from '../services/warrantyService';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizations } from './useOrganizations';

export const useWarranties = (organizationId?: string) => {
  const { user } = useAuth();
  const { organizations } = useOrganizations();
  const [warranties, setWarranties] = useState<Warranty[]>([]);
  const [claims, setClaims] = useState<WarrantyClaim[]>([]);
  const [stats, setStats] = useState<WarrantyStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use the first organization if no organizationId is provided
  const currentOrgId = organizationId || organizations[0]?.id;

  // Load warranties
  const loadWarranties = useCallback(async (params: WarrantyQueryParams = {}) => {
    if (!currentOrgId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await warrantyService.getWarranties(currentOrgId, params);
      setWarranties(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load warranties');
      console.error('Error loading warranties:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrgId]);

  // Load warranty claims
  const loadClaims = useCallback(async (params: WarrantyClaimQueryParams = {}) => {
    if (!currentOrgId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await warrantyService.getWarrantyClaims(currentOrgId, params);
      setClaims(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load warranty claims');
      console.error('Error loading warranty claims:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrgId]);

  // Load statistics
  const loadStats = useCallback(async () => {
    if (!currentOrgId) return;

    try {
      const data = await warrantyService.getWarrantyStats(currentOrgId);
      setStats(data);
    } catch (err) {
      console.error('Error loading warranty stats:', err);
    }
  }, [currentOrgId]);

  // Create warranty
  const createWarranty = useCallback(async (data: CreateWarrantyData) => {
    if (!currentOrgId || !user?.id) {
      throw new Error('Missing organization or user');
    }

    setLoading(true);
    setError(null);
    try {
      const id = await warrantyService.createWarranty(data, currentOrgId, user.id);
      await loadWarranties();
      await loadStats();
      return id;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create warranty');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentOrgId, user?.id, loadWarranties, loadStats]);

  // Update warranty
  const updateWarranty = useCallback(async (id: string, data: Partial<Warranty>) => {
    setLoading(true);
    setError(null);
    try {
      await warrantyService.updateWarranty(id, data);
      await loadWarranties();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update warranty');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadWarranties, loadStats]);

  // Delete warranty
  const deleteWarranty = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await warrantyService.deleteWarranty(id);
      await loadWarranties();
      await loadClaims();
      await loadStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete warranty');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadWarranties, loadClaims, loadStats]);

  // Create warranty claim
  const createWarrantyClaim = useCallback(async (data: CreateWarrantyClaimData) => {
    if (!currentOrgId || !user?.id) {
      throw new Error('Missing organization or user');
    }

    setLoading(true);
    setError(null);
    try {
      const id = await warrantyService.createWarrantyClaim(data, currentOrgId, user.id);
      await loadClaims();
      await loadWarranties(); // Refresh warranties to update claims count
      await loadStats();
      return id;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create warranty claim');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentOrgId, user?.id, loadClaims, loadWarranties, loadStats]);

  // Update expired warranties
  const updateExpiredWarranties = useCallback(async () => {
    if (!currentOrgId) return;

    try {
      await warrantyService.updateExpiredWarranties(currentOrgId);
      await loadWarranties();
      await loadStats();
    } catch (err) {
      console.error('Error updating expired warranties:', err);
    }
  }, [currentOrgId, loadWarranties, loadStats]);

  // Load data on mount and organization change
  useEffect(() => {
    if (currentOrgId) {
      loadWarranties();
      loadClaims();
      loadStats();
      updateExpiredWarranties();
    }
  }, [currentOrgId, loadWarranties, loadClaims, loadStats, updateExpiredWarranties]);

  return {
    warranties,
    claims,
    stats,
    loading,
    error,
    loadWarranties,
    loadClaims,
    loadStats,
    createWarranty,
    updateWarranty,
    deleteWarranty,
    createWarrantyClaim,
    updateExpiredWarranties,
    refresh: () => {
      loadWarranties();
      loadClaims();
      loadStats();
    }
  };
}; 